import { useEffect, useMemo, useState, useCallback } from "react";
import { api, ApiError } from "../api/client";
import SavedMatchesTab from "../components/SavedMatchesTab";
import { BOOK_LABELS } from "../types/api";
import type { AdminUser, Book } from "../types/api";

// The /superadmin panel. Reached only by typing the URL (never linked from the app).
// This is NOT a security boundary — the real gate is require_admin on /api/admin/* (403
// for non-admins). Here we: (1) show a login if not signed in, (2) bounce a signed-in
// NON-admin with a plain "not authorized" message, (3) render user management for admins.
//
// Organised as four tabs so each answers one question:
//   Users      — who has access, and to what. Everything that CHANGES a person.
//   Onboarding — which insurers exist in each book, verified vs still under testing.
//                Read-only reference: the "what is where" view.
//   Saved matches — every saved match across insurers, current and removed. Read-only.
//   My account — the admin's own password.
export default function SuperAdminPage() {
  // null = still checking /api/me; "anon" = not logged in; "user"/"admin" = role.
  const [phase, setPhase] = useState<"checking" | "anon" | "denied" | "admin">("checking");
  const [me, setMe] = useState<{ username: string } | null>(null);

  const check = useCallback(async () => {
    try {
      const r = await api.me();
      if (r.role === "admin") {
        setMe({ username: r.username });
        setPhase("admin");
      } else {
        setPhase("denied");
      }
    } catch {
      setPhase("anon");
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  if (phase === "checking") {
    return <Centered><span className="muted">Loading…</span></Centered>;
  }
  if (phase === "anon") {
    return <AdminLogin onLoggedIn={check} />;
  }
  if (phase === "denied") {
    return (
      <Centered>
        <div className="card" style={{ padding: 32, maxWidth: 420, textAlign: "center" }}>
          <h1 style={{ fontSize: "1.1rem", marginTop: 0 }}>Not authorized</h1>
          <p className="muted">This area requires a superadmin account.</p>
          <a className="btn" href="/">Back to app</a>
        </div>
      </Centered>
    );
  }
  return <AdminPanel me={me!} onPasswordChanged={check} />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      {children}
    </div>
  );
}

// A dedicated login surface for /superadmin. Same POST /api/login as the normal login;
// after success we re-check the role (a non-admin who logs in here is bounced to "denied").
function AdminLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(username, password);
      onLoggedIn();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 401
        ? "Invalid credentials."
        : "Couldn't sign in — the server may be unavailable. Try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Centered>
      <form onSubmit={submit} className="card" style={{ width: 340, padding: 32 }}>
        <h1 style={{ fontSize: "1.1rem", margin: "0 0 6px" }}>Superadmin</h1>
        <p className="muted" style={{ marginTop: 0, fontSize: "0.85rem" }}>Restricted — user management.</p>
        {error && <div className="badge badge-risk" style={{ display: "block", marginBottom: 12 }}>{error}</div>}
        <label className="field-label" style={{ marginTop: 0 }}>Username</label>
        <input className="input" autoFocus autoComplete="username"
          value={username} onChange={(e) => setUsername(e.target.value)} />
        <label className="field-label">Password</label>
        <input className="input" type="password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} />
        <button className="btn btn-primary" type="submit" disabled={busy || !username || !password}
          style={{ width: "100%", marginTop: 20 }}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </Centered>
  );
}

// A user's book scope as the SERVER reads it: null/absent means the default book only
// (AP), which is what every account created before the AR book existed carries. Kept in
// one place so the display, the editor and the save path cannot disagree.
function booksOf(user: AdminUser, allBooks: Book[]): Book[] {
  if (user.role === "admin") return allBooks;          // admins hold every book
  const stored = user.allowed_books;
  if (stored === null || stored === undefined) return ["AP"];
  return allBooks.filter((b) => stored.includes(b));
}

type Tab = "users" | "onboarding" | "saved" | "account";

function AdminPanel({ me, onPasswordChanged }: {
  me: { username: string };
  onPasswordChanged: () => void;
}) {
  const [tab, setTab] = useState<Tab>("users");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [allInsurers, setAllInsurers] = useState<string[]>([]);
  const [testingInsurers, setTestingInsurers] = useState<string[]>([]);
  const [books, setBooks] = useState<Book[]>(["AP"]);
  const [insurersByBook, setInsurersByBook] = useState<Record<string, string[]>>({});
  const [testingByBook, setTestingByBook] = useState<Record<string, string[]>>({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const TABS: { id: Tab; label: string; count?: number }[] = [
    { id: "users", label: "Users", count: users.length },
    { id: "onboarding", label: "Onboarding", count: allInsurers.length },
    { id: "saved", label: "Saved matches" },
    { id: "account", label: "My account" },
  ];

  const reload = useCallback(async () => {
    try {
      const r = await api.adminListUsers();
      setUsers(r.users);
      setAllInsurers(r.all_insurers);
      setTestingInsurers(r.testing_insurers ?? []);
      // Older backend: no book fields. Fall back to AP-only so the panel still works.
      setBooks(r.books ?? ["AP"]);
      setInsurersByBook(r.insurers_by_book ?? { AP: r.all_insurers });
      setTestingByBook(r.testing_insurers_by_book ?? { AP: r.testing_insurers ?? [] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users.");
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  function flash(msg: string) {
    setNotice(msg);
    setError("");
    window.setTimeout(() => setNotice(""), 4000);
  }
  function fail(e: unknown) {
    setError(e instanceof Error ? e.message : "Request failed.");
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 20px" }}>
      <header className="page-head">
        <strong style={{ color: "var(--primary)", fontSize: "1.1rem" }}>Superadmin</strong>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span className="muted">{me.username}</span>
          <a className="btn" href="/">Back to app</a>
          <button className="btn" onClick={async () => { await api.logout(); window.location.reload(); }}>Sign out</button>
        </div>
      </header>

      {/* Full WAI-ARIA tabs, same pattern as AdditionalDetails.tsx: only the ACTIVE tab
          is tabbable (roving tabindex) so Tab enters/leaves the strip in one step and
          Left/Right move between tabs. */}
      <div className="tabstrip" role="tablist" aria-label="Superadmin sections">
        {TABS.map(({ id, label, count }, i) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`satab-${id}`}
            aria-selected={tab === id}
            aria-controls={`satabpanel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            className={`tab ${tab === id ? "active" : ""}`}
            onClick={() => setTab(id)}
            onKeyDown={(e) => {
              if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" &&
                  e.key !== "Home" && e.key !== "End") return;
              e.preventDefault();
              const n = TABS.length;
              const next =
                e.key === "Home" ? 0
                : e.key === "End" ? n - 1
                : e.key === "ArrowRight" ? (i + 1) % n
                : (i - 1 + n) % n;
              setTab(TABS[next].id);
              document.getElementById(`satab-${TABS[next].id}`)?.focus();
            }}
          >
            {label}
            {count !== undefined && <span className="tab-count">{count}</span>}
          </button>
        ))}
      </div>

      {error && <div className="badge badge-risk" style={{ display: "block", marginBottom: 16 }}>{error}</div>}
      {notice && <div className="badge badge-success" style={{ display: "block", marginBottom: 16 }}>{notice}</div>}

      <div role="tabpanel" id={`satabpanel-${tab}`} aria-labelledby={`satab-${tab}`}>
        {tab === "users" && (
          <UsersTab
            users={users} allInsurers={allInsurers} testingInsurers={testingInsurers}
            books={books}
            onChanged={(msg) => { flash(msg); reload(); }} onError={fail}
          />
        )}

        {tab === "onboarding" && (
          <OnboardingTab books={books} insurersByBook={insurersByBook}
            testingByBook={testingByBook} users={users} />
        )}

        {tab === "saved" && <SavedMatchesTab />}

        {tab === "account" && (
          <ChangeMyPassword
            onChanged={() => { flash("Your password was changed."); onPasswordChanged(); }}
            onError={fail} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
function UsersTab({ users, allInsurers, testingInsurers, books, onChanged, onError }: {
  users: AdminUser[];
  allInsurers: string[];
  testingInsurers: string[];
  books: Book[];
  onChanged: (msg: string) => void;
  onError: (e: unknown) => void;
}) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? users.filter((u) => u.username.toLowerCase().includes(q)) : users;
  }, [users, query]);

  // Headline counts, so the admin can see the shape of the account base at a glance
  // instead of reading every row.
  const stats = useMemo(() => {
    const byBook: Record<string, number> = {};
    let admins = 0, disabled = 0;
    for (const u of users) {
      if (u.role === "admin") admins += 1;
      if (u.disabled) disabled += 1;
      for (const b of booksOf(u, books)) byBook[b] = (byBook[b] ?? 0) + 1;
    }
    return { byBook, admins, disabled };
  }, [users, books]);

  return (
    <>
      <section className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 12 }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: "1rem" }}>Accounts</h2>
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? "Cancel" : "New user"}
          </button>
        </div>
        <div className="chip-row" style={{ marginBottom: 12 }}>
          <span className="chip">{users.length} total</span>
          {books.map((b) => (
            <span className="chip" key={b}>{stats.byBook[b] ?? 0} can use {b}</span>
          ))}
          <span className="chip">{stats.admins} admin</span>
          {stats.disabled > 0 && <span className="badge badge-warn">{stats.disabled} disabled</span>}
        </div>
        <input className="input" placeholder="Filter by username…" value={query}
          onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 280 }} />

        {creating && (
          <CreateUser allInsurers={allInsurers} testingInsurers={testingInsurers} books={books}
            onCreated={(u) => { setCreating(false); onChanged(`Created user “${u}”.`); }}
            onError={onError} />
        )}
      </section>

      <section className="card" style={{ padding: 20 }}>
        {shown.map((u, i) => (
          <UserRow key={u.username} user={u} allInsurers={allInsurers}
            testingInsurers={testingInsurers} books={books} first={i === 0}
            onChanged={onChanged} onError={onError} />
        ))}
        {shown.length === 0 && (
          <p className="muted">{users.length === 0 ? "No users." : "No user matches that filter."}</p>
        )}
      </section>
    </>
  );
}

function ChangeMyPassword({ onChanged, onError }: { onChanged: () => void; onError: (e: unknown) => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setCurrent(""); setNext("");
      onChanged();
    } catch (err) { onError(err); }
    finally { setBusy(false); }
  }

  return (
    <section className="card" style={{ padding: 20, marginBottom: 16 }}>
      <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Change my password</h2>
      <p className="muted note-block-sm">
        Your own account only. Other people's passwords are reset from their row under Users.
      </p>
      <form onSubmit={submit} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label className="field-label" style={{ marginTop: 0 }}>Current</label>
          <input className="input" type="password" autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div>
          <label className="field-label" style={{ marginTop: 0 }}>New (min 12 chars)</label>
          <input className="input" type="password" autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy || !current || next.length < 12}>
          {busy ? "Saving…" : "Change"}
        </button>
      </form>
    </section>
  );
}

function CreateUser({ allInsurers, testingInsurers, books, onCreated, onError }: {
  allInsurers: string[];
  testingInsurers: string[];
  books: Book[];
  onCreated: (username: string) => void;
  onError: (e: unknown) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  // null = all-access (default). A Set means "pinned to these".
  const [pinned, setPinned] = useState<Set<string> | null>(null);
  // Least privilege: a new account gets the default book only until the admin says more.
  const [picked, setPicked] = useState<Book[]>(["AP"]);
  const [gemini, setGemini] = useState(false);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.adminCreateUser({
        username, password, role,
        allowed_insurers: pinned === null ? null : [...pinned],
        allowed_books: picked,
        can_use_gemini: gemini,
        can_use_testing_insurers: testing,
      });
      setUsername(""); setPassword(""); setRole("user"); setPinned(null);
      setPicked(["AP"]); setGemini(false); setTesting(false);
      onCreated(username);
    } catch (err) { onError(err); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
      <h3 style={{ fontSize: "0.95rem", margin: "0 0 10px" }}>New user</h3>
      <form onSubmit={submit}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label className="field-label" style={{ marginTop: 0 }}>Username</label>
            <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="field-label" style={{ marginTop: 0 }}>Password (min 12)</label>
            <input className="input" type="password" autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <label className="field-label" style={{ marginTop: 0 }}>Role</label>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
        </div>

        <PermissionBlock
          title="Reconciliation books"
          hint="Which side of the ledger this account works on. An account sees only the books it holds — no book picker appears at all when it holds one."
        >
          <BookScope books={books} picked={picked} onChange={setPicked} isAdmin={role === "admin"} />
        </PermissionBlock>

        <PermissionBlock
          title="Insurers"
          hint="Applies to every book. An insurer must also be onboarded in the book being used."
        >
          <InsurerScope allInsurers={allInsurers} testingInsurers={testingInsurers}
            pinned={pinned} onChange={setPinned} />
        </PermissionBlock>

        <PermissionBlock title="Extra permissions" hint="All are off by default.">
          {testingInsurers.length > 0 && (
            <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={testing} onChange={(e) => setTesting(e.target.checked)} />
              <span>Insurers under testing <span className="muted">— not finance-verified; figures may change</span></span>
            </label>
          )}
          <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer", marginTop: 8 }}>
            <input type="checkbox" checked={gemini} onChange={(e) => setGemini(e.target.checked)} />
            <span>Layout detection for new insurers <span className="muted">— paid Gemini API, sends data to Google</span></span>
          </label>
        </PermissionBlock>

        <button className="btn btn-primary" type="submit"
          disabled={busy || !username || password.length < 12 || picked.length === 0}
          style={{ marginTop: 14 }}>
          {busy ? "Creating…" : "Create user"}
        </button>
        {picked.length === 0 && (
          <span className="muted" style={{ marginLeft: 10 }}>Pick at least one book.</span>
        )}
      </form>
    </div>
  );
}

// A labelled group inside the permission editor, so "what is where" is legible without
// reading the checkboxes themselves.
function PermissionBlock({ title, hint, children }: {
  title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <div className="field-label" style={{ marginTop: 0 }}>{title}</div>
      {hint && <p className="muted note-block-sm" style={{ marginTop: 2 }}>{hint}</p>}
      {children}
    </div>
  );
}

// Book picker. An admin implicitly holds every book, so the control is shown ticked and
// disabled for them rather than pretending it can be narrowed.
function BookScope({ books, picked, onChange, isAdmin }: {
  books: Book[];
  picked: Book[];
  onChange: (v: Book[]) => void;
  isAdmin: boolean;
}) {
  function toggle(book: Book) {
    const next = picked.includes(book)
      ? picked.filter((b) => b !== book)
      : [...picked, book];
    onChange(books.filter((b) => next.includes(b)));   // keep display order
  }
  return (
    <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
      {books.map((b) => (
        <label key={b} style={{ display: "flex", gap: 6, alignItems: "center",
          cursor: isAdmin ? "default" : "pointer", opacity: isAdmin ? 0.6 : 1 }}>
          <input type="checkbox" checked={isAdmin || picked.includes(b)} disabled={isAdmin}
            onChange={() => toggle(b)} />
          <span>
            <strong>{b}</strong> <span className="muted">{BOOK_LABELS[b] ?? b}</span>
          </span>
        </label>
      ))}
      {isAdmin && <span className="muted">admins always hold every book</span>}
    </div>
  );
}

// Shared insurer-scope editor: an "all insurers" toggle plus per-insurer checkboxes.
// pinned===null renders as all-access; toggling any box switches to an explicit set.
function InsurerScope({ allInsurers, testingInsurers, pinned, onChange }: {
  allInsurers: string[];
  testingInsurers: string[];
  pinned: Set<string> | null;
  onChange: (v: Set<string> | null) => void;
}) {
  const allAccess = pinned === null;
  const isTesting = new Set(testingInsurers);
  function toggle(name: string) {
    const base = pinned === null ? new Set(allInsurers) : new Set(pinned);
    if (base.has(name)) base.delete(name); else base.add(name);
    onChange(base);
  }
  return (
    <div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
        <input type="checkbox" checked={allAccess}
          onChange={(e) => onChange(e.target.checked ? null : new Set(allInsurers))} />
        <span>
          All insurers (including any added in future)
          {testingInsurers.length > 0 && (
            <span className="muted"> — testing insurers still need their own permission</span>
          )}
        </span>
      </label>
      {!allAccess && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8, paddingLeft: 24 }}>
          {allInsurers.map((name) => (
            <label key={name} style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={pinned!.has(name)} onChange={() => toggle(name)} />
              <span>
                {name}
                {isTesting.has(name) && <span className="muted" title="Not finance-verified"> · testing</span>}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function UserRow({ user, allInsurers, testingInsurers, books, first, onChanged, onError }: {
  user: AdminUser;
  allInsurers: string[];
  testingInsurers: string[];
  books: Book[];
  // The row separator is a TOP border, so the first row would otherwise draw a stray
  // rule immediately under the card's own edge.
  first?: boolean;
  onChanged: (msg: string) => void;
  onError: (e: unknown) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [pinned, setPinned] = useState<Set<string> | null>(
    user.allowed_insurers === null ? null : new Set(user.allowed_insurers)
  );
  const [picked, setPicked] = useState<Book[]>(booksOf(user, books));
  const [gemini, setGemini] = useState<boolean>(!!user.can_use_gemini);
  const [testing, setTesting] = useState<boolean>(!!user.can_use_testing_insurers);
  // Re-sync the local state whenever the persisted value changes (after a reload, React
  // reuses this row instance by its key= so the useState initializer does NOT re-run —
  // without this, the controls would show stale state after a save/reload).
  useEffect(() => {
    setPinned(user.allowed_insurers === null ? null : new Set(user.allowed_insurers));
  }, [user.allowed_insurers]);
  useEffect(() => { setGemini(!!user.can_use_gemini); }, [user.can_use_gemini]);
  useEffect(() => { setTesting(!!user.can_use_testing_insurers); }, [user.can_use_testing_insurers]);
  useEffect(() => { setPicked(booksOf(user, books)); }, [user.allowed_books, user.role, books]);
  // An admin always has every permission implicitly; the toggles are only meaningful for
  // regular users (and would be a no-op for an admin).
  const isAdmin = user.role === "admin";
  const heldBooks = booksOf(user, books);

  async function doReset() {
    try { await api.adminResetPassword(user.username, resetPw); setResetPw(""); onChanged(`Reset password for “${user.username}”.`); }
    catch (e) { onError(e); }
  }
  async function saveAccess() {
    try {
      await api.adminSetInsurers(user.username, pinned === null ? null : [...pinned]);
      // Books: no-op server-side for admins (who always hold every book), so only send
      // for regular users, and only when the selection actually moved.
      const sameBooks = picked.length === heldBooks.length
        && picked.every((b) => heldBooks.includes(b));
      if (!isAdmin && !sameBooks) {
        await api.adminSetBooks(user.username, picked);
      }
      // Same for the Gemini flag and the testing tier — admins are implicitly allowed.
      if (!isAdmin && gemini !== !!user.can_use_gemini) {
        await api.adminSetGemini(user.username, gemini);
      }
      if (!isAdmin && testing !== !!user.can_use_testing_insurers) {
        await api.adminSetTestingInsurers(user.username, testing);
      }
      onChanged(`Updated access for “${user.username}”.`);
    } catch (e) { onError(e); }
  }
  async function toggleDisabled() {
    try { await api.adminSetDisabled(user.username, !user.disabled); onChanged(`${user.disabled ? "Enabled" : "Disabled"} “${user.username}”.`); }
    catch (e) { onError(e); }
  }
  async function doDelete() {
    // Irreversible: removes the account row and all its sessions. Hard-confirm first.
    if (!window.confirm(`Permanently delete “${user.username}”? This cannot be undone.`)) return;
    try { await api.adminDeleteUser(user.username); onChanged(`Deleted “${user.username}”.`); }
    catch (e) { onError(e); }
  }

  const scope = user.allowed_insurers === null ? "all insurers"
    : user.allowed_insurers.length === 0 ? "no insurers"
    : `${user.allowed_insurers.length} insurer(s)`;

  return (
    <div style={{ borderTop: first ? "none" : "1px solid var(--border)",
      padding: first ? "0 0 12px" : "12px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <strong>{user.username}</strong>{" "}
          <span className="chip-row" style={{ display: "inline-flex", marginLeft: 6, verticalAlign: "middle" }}>
            {heldBooks.map((b) => <span className="chip" key={b}>{b}</span>)}
            {isAdmin && <span className="badge badge-inline">admin</span>}
            {user.disabled && <span className="badge badge-risk">disabled</span>}
          </span>
          <div className="muted" style={{ fontSize: "0.85rem", marginTop: 2 }}>
            {scope}
            {(isAdmin || user.can_use_testing_insurers) && " · testing tier"}
            {(isAdmin || user.can_use_gemini) && " · new-insurer detection"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={toggleDisabled}>{user.disabled ? "Enable" : "Disable"}</button>
          <button className="btn btn-danger" onClick={doDelete}>Delete</button>
          <button className="btn" onClick={() => setExpanded((v) => !v)}>{expanded ? "Close" : "Manage"}</button>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, paddingLeft: 4 }}>
          <PermissionBlock
            title="Reconciliation books"
            hint="Revoking a book takes effect on this user's next request — they do not need to sign out."
          >
            <BookScope books={books} picked={picked} onChange={setPicked} isAdmin={isAdmin} />
          </PermissionBlock>

          <PermissionBlock title="Insurers" hint="Applies to every book this account holds.">
            <InsurerScope allInsurers={allInsurers} testingInsurers={testingInsurers}
              pinned={pinned} onChange={setPinned} />
          </PermissionBlock>

          <PermissionBlock title="Extra permissions">
            {testingInsurers.length > 0 && (
              <label style={{ display: "flex", gap: 8, alignItems: "center",
                cursor: isAdmin ? "default" : "pointer", opacity: isAdmin ? 0.6 : 1 }}>
                <input type="checkbox" checked={isAdmin || testing} disabled={isAdmin}
                  onChange={(e) => setTesting(e.target.checked)} />
                <span>Insurers under testing
                  {isAdmin && <span className="muted"> — admins always allowed</span>}
                  {!isAdmin && <span className="muted"> — not finance-verified; figures may change</span>}
                </span>
              </label>
            )}
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8,
              cursor: isAdmin ? "default" : "pointer", opacity: isAdmin ? 0.6 : 1 }}>
              <input type="checkbox" checked={isAdmin || gemini} disabled={isAdmin}
                onChange={(e) => setGemini(e.target.checked)} />
              <span>Layout detection for new insurers
                {isAdmin && <span className="muted"> — admins always allowed</span>}
                {!isAdmin && <span className="muted"> — paid Gemini API, sends data to Google</span>}
              </span>
            </label>
          </PermissionBlock>

          <button className="btn btn-primary" onClick={saveAccess} style={{ marginTop: 14 }}
            disabled={!isAdmin && picked.length === 0}>
            Save access
          </button>
          {!isAdmin && picked.length === 0 && (
            <span className="muted" style={{ marginLeft: 10 }}>
              Pick at least one book — an account with none can do nothing.
            </span>
          )}

          <hr className="rule-section" />
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div>
              <label className="field-label" style={{ marginTop: 0 }}>Reset password (min 12)</label>
              <input className="input" type="password" autoComplete="new-password"
                value={resetPw} onChange={(e) => setResetPw(e.target.value)} />
            </div>
            <button className="btn" onClick={doReset} disabled={resetPw.length < 12}>Reset</button>
          </div>
          <p className="muted note">Resetting signs the user out of every active session.</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onboarding — read-only reference
// ---------------------------------------------------------------------------
function OnboardingTab({ books, insurersByBook, testingByBook, users }: {
  books: Book[];
  insurersByBook: Record<string, string[]>;
  testingByBook: Record<string, string[]>;
  users: AdminUser[];
}) {
  return (
    <>
      <section className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Insurer onboarding by book</h2>
        <p className="muted note-block-sm">
          Each book has its own layouts: an insurer onboarded for AP is not thereby
          onboarded for AR. <strong>Verified</strong> insurers are finance-signed-off and
          their figures must not change; <strong>testing</strong> insurers are still being
          proven and are visible only to accounts holding that permission.
        </p>
        {books.map((b) => {
          const all = insurersByBook[b] ?? [];
          const testing = new Set(testingByBook[b] ?? []);
          const verified = all.filter((n) => !testing.has(n));
          return (
            <div key={b} style={{ marginTop: 16 }}>
              <div className="page-head" style={{ marginBottom: 6 }}>
                <strong>{b} <span className="muted">· {BOOK_LABELS[b] ?? b}</span></strong>
                <span className="chip-row">
                  <span className="chip">{verified.length} verified</span>
                  <span className="chip">{testing.size} testing</span>
                </span>
              </div>
              {all.length === 0 ? (
                <p className="muted">No insurer is onboarded for {b} yet.</p>
              ) : (
                <div className="chip-row">
                  {verified.map((n) => <span className="badge badge-success" key={n}>{n}</span>)}
                  {[...testing].sort().map((n) => (
                    <span className="badge badge-warn" key={n} title="Not finance-verified">{n}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="card" style={{ padding: 20 }}>
        <h2 style={{ fontSize: "1rem", marginTop: 0 }}>Who can use each book</h2>
        {books.map((b) => {
          const holders = users.filter((u) => booksOf(u, books).includes(b));
          return (
            <div key={b} style={{ marginTop: 12 }}>
              <div className="field-label" style={{ marginTop: 0 }}>{b} — {holders.length} account(s)</div>
              {holders.length === 0 ? (
                <p className="muted">Nobody holds {b} yet. Grant it from a user's row under Users.</p>
              ) : (
                <div className="chip-row">
                  {holders.map((u) => (
                    <span className="chip" key={u.username}>
                      {u.username}{u.disabled ? " (disabled)" : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <p className="muted note">
          The calling admin is not listed here — this panel never shows your own account.
        </p>
      </section>
    </>
  );
}
